import React, { useCallback, useEffect, useRef, useState } from "react";
import videoJs from "video.js";
import Player from "video.js/dist/types/player";
import "video.js/dist/video-js.min.css";
import { IAgoraTrack } from "../../helpers/types";
import "./index.less";

interface props extends IAgoraTrack {
  isBlur?: boolean;
  index?: number;
  sessionId?: string;
  isFromMobile: boolean;
  mirror?: boolean;
  objectFit?: "cover" | "contain";
  showControls?: boolean;
}
const getPlayerOptions = (showControls: boolean) => ({
  bigPlayButton: false,
  controls: showControls,
  autoPlay: true,
  playsInline: true,
  muted: true,
  fluid: false,
  fill: true,
  controlBar: showControls
    ? {
        playToggle: false,
        fullscreenToggle: true,
        pictureInPictureToggle: false,
        volumePanel: true,
        currentTimeDisplay: false,
        timeDivider: false,
        durationDisplay: false,
        remainingTimeDisplay: false,
        progressControl: false,
      }
    : {
        playToggle: false,
        fullscreenToggle: false,
        pictureInPictureToggle: false,
        volumePanel: false,
      },
});

const AgoraPlayerComponent: React.FC<props> = ({
  tracks,
  isBlur,
  index,
  sessionId,
  isFromMobile,
  mirror,
  objectFit = "cover",
  showControls = false,
}) => {
  const playerRef = useRef<HTMLVideoElement>(null);
  const ref = useRef<Player>();
  const streamRef = useRef<MediaStream | null>(null);
  const tracksRef = useRef<props["tracks"]>([]);
  const [isRefUpdated, setIsRefUpdated] = useState<boolean>(false);
  const [needsResume, setNeedsResume] = useState<boolean>(false);

  useEffect(() => {
    if (!playerRef?.current) return;
    ref.current = videoJs(playerRef.current, getPlayerOptions(showControls));
    setIsRefUpdated(true);
  }, []);

  const applyVideoSizing = (videoEl: HTMLVideoElement, fit: string) => {
    videoEl.style.setProperty("object-fit", fit, "important");
    videoEl.style.setProperty("-webkit-object-fit", fit, "important");
    videoEl.style.setProperty("position", "absolute", "important");
    videoEl.style.setProperty("top", "0", "important");
    videoEl.style.setProperty("left", "0", "important");
    videoEl.style.setProperty("width", "100%", "important");
    videoEl.style.setProperty("height", "100%", "important");
    // Force reflow
    void videoEl.offsetHeight;
  };

  const resumeStream = useCallback((fit: string) => {
    if (!tracksRef.current?.length || !ref.current) return;
    const videoEl = ref.current.tech().el() as HTMLVideoElement;
    const freshTracks = tracksRef.current.map((t) => t.getMediaStreamTrack());
    const freshStream = new MediaStream(freshTracks);
    streamRef.current = freshStream;
    videoEl.srcObject = freshStream;
    applyVideoSizing(videoEl, fit);
    videoEl
      .play()
      .then(() => {
        // iOS can silently resolve play() even on ended/empty tracks — verify real video data
        if (videoEl.readyState < 2) {
          setNeedsResume(true);
        } else {
          setNeedsResume(false);
        }
      })
      .catch(() => {
        setNeedsResume(true);
      });
  }, []);

  useEffect(() => {
    if (!isRefUpdated || !ref?.current) return;

    if (tracks.length) {
      tracksRef.current = tracks;
      const mediaStreamTracks: MediaStreamTrack[] = tracks.map((track) => track.getMediaStreamTrack());
      const videoEl = ref.current.tech().el() as HTMLVideoElement;
      const stream = new MediaStream(mediaStreamTracks);
      streamRef.current = stream;
      videoEl.srcObject = stream;

      videoEl
        .play()
        .then(() => setNeedsResume(false))
        .catch(() => setNeedsResume(true));

      // Apply mirror transform for front camera
      if (mirror) {
        videoEl.style.transform = "rotateY(180deg)";
      }

      applyVideoSizing(videoEl, objectFit);

      // Resume playback and reapply sizing after standard fullscreen exit
      const handleFullscreenChange = () => {
        const isFullscreen =
          !!(document as any).fullscreenElement ||
          !!(document as any).webkitFullscreenElement ||
          !!(document as any).mozFullScreenElement ||
          !!(document as any).msFullscreenElement;

        if (!isFullscreen) {
          // Show overlay immediately — auto-resume attempt follows, overlay hides only if video data confirmed
          setNeedsResume(true);
          setTimeout(() => resumeStream(objectFit), 400);
        }
      };

      // Resume playback after iOS native fullscreen exit
      const handleWebkitEndFullscreen = () => {
        // Show overlay immediately — iOS always pauses and may end tracks on native fullscreen exit
        setNeedsResume(true);
        setTimeout(() => resumeStream(objectFit), 400);
      };

      document.addEventListener("fullscreenchange", handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.addEventListener("mozfullscreenchange", handleFullscreenChange);
      document.addEventListener("MSFullscreenChange", handleFullscreenChange);
      videoEl.addEventListener("webkitendfullscreen", handleWebkitEndFullscreen);

      // Resize observer to ensure video fills container
      const resizeObserver = new ResizeObserver(() => {
        const isFullscreen =
          !!(document as any).fullscreenElement ||
          !!(document as any).webkitFullscreenElement ||
          !!(document as any).mozFullScreenElement ||
          !!(document as any).msFullscreenElement;
        if (!isFullscreen) {
          applyVideoSizing(videoEl, objectFit);
        }
      });

      if (playerRef.current?.parentElement) {
        resizeObserver.observe(playerRef.current.parentElement);
      }

      return () => {
        resizeObserver.disconnect();
        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
        document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
        videoEl.removeEventListener("webkitendfullscreen", handleWebkitEndFullscreen);
      };
    } else {
      (ref.current.tech().el() as HTMLVideoElement).srcObject = null;
      ref.current.tech_.el_.style.objectFit = "contain";
      ref.current.tech_.el_.style.transform = "none";
    }
  }, [isRefUpdated, tracks, mirror, isFromMobile, objectFit]);

  useEffect(() => {
    if (index !== undefined && !isNaN(index)) {
      const videoElement = document.getElementById(`video-js-${index}`);
      if (videoElement) {
        if (isBlur && !videoElement.classList.contains("blur")) videoElement.classList.add("blur");
        if (!isBlur && videoElement.classList.contains("blur")) videoElement.classList.remove("blur");
      }
    }
  }, [isBlur]);

  const handleClickResume = () => {
    resumeStream(objectFit);
  };

  const videoEl = (
    <video id={`video-js-${index}`} className={`vjs-matrix video-js`} ref={playerRef} autoPlay width="100%" height="100%" playsInline muted />
  );

  if (!showControls) {
    return videoEl;
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {videoEl}
      {needsResume && (
        <div
          onClick={handleClickResume}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.45)",
            cursor: "pointer",
            zIndex: 30,
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              border: "2px solid rgba(255,255,255,0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgoraPlayerComponent;
